import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError, UnauthenticatedError } from "@/lib/api/client";

/**
 * Los valores por defecto de la caché.
 *
 * ## `staleTime: 0` es a propósito
 *
 * El código anterior pedía todo con `cache: "no-store"`: cada visita a una
 * pantalla traía datos frescos. Un CRM que muestra un lead con la etapa vieja
 * hace que alguien lo trabaje dos veces, así que esa decisión se respeta.
 *
 * Lo que igual se gana es la navegación: con `gcTime` alto, volver a una
 * pantalla ya visitada **pinta al instante lo que había** y refresca por
 * detrás, en vez de dejar el esqueleto tres segundos. Datos frescos y
 * navegación instantánea no están peleados; lo que hay que evitar es *creer*
 * viejo lo que se muestra, no mostrarlo mientras llega lo nuevo.
 *
 * ## Los reintentos
 *
 * Un 4xx no mejora repitiéndolo: un 404 sigue siendo 404 y un 422 tiene el
 * cuerpo mal. Reintentarlos solo multiplica una espera que acá cuesta caro,
 * porque cada viaje a la base son cientos de milisegundos. Se reintenta una
 * sola vez, y únicamente lo que puede ser un problema de red.
 *
 * ## La sesión vencida manda al login, no a un error
 *
 * `apiFetch` ya intenta renovar antes de rendirse, así que un
 * `UnauthenticatedError` que llega hasta acá significa que la sesión se terminó
 * de verdad. Sin esto, lo que veía la persona era un error distinto en cada
 * pantalla —«La API respondió 401» al guardar, la lista vacía al mirar— y la
 * conclusión natural era que la aplicación se había roto.
 *
 * Se navega con `location.assign` y no con el router porque esto vive fuera de
 * React: el cliente de consultas se crea una vez, antes de que exista un
 * `navigate`. Y recargar entero es lo correcto cuando la sesión cambió: no
 * queda ni un dato del usuario anterior en memoria.
 */
function alVencerLaSesion(error: unknown): void {
  if (!(error instanceof UnauthenticatedError)) return;
  if (window.location.pathname === "/login") return;
  window.location.assign("/login");
}
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: alVencerLaSesion }),
    mutationCache: new MutationCache({ onError: alVencerLaSesion }),
    defaultOptions: {
      queries: {
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        /* Sin esto, volver a la pestaña dispara todas las consultas de la
         * pantalla. Con la latencia que tenemos eso es medio segundo de
         * esqueletos cada vez que alguien vuelve del correo. */
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof UnauthenticatedError) return false;
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 1;
        },
      },
      mutations: {
        // Una mutación reintentada puede duplicar un envío o una nota.
        retry: false,
      },
    },
  });
}
